import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './VideoTemplate.css';
import ResultDisplay from '../components/ResultDisplay';
import { RedButton, BackArrow } from '../components/common';
import SupportEmail from '../components/SupportEmail';
import LegalNotice from '../components/LegalNotice';
import { videoActionsConfig, defaultVideoAction } from '../data/videoActionsConfig';
import { fetchUserPhotos } from '@genfun/core/api/API';
import { useVideoAnimate } from '@genfun/core/hooks/useVideoAnimate';
import { usePhotoGenerate } from '@genfun/core/hooks/usePhotoGenerate';
import { findActionPath } from '@genfun/core/utils/common';
import { scrollNear, scrollNearAfterRender } from '../utils/autoScroll';
import formSchemaVideo from '@genfun/core/data/formSchemaVideo.json';

const CDN_VIDEO_BASE = "https://genres.nyc3.cdn.digitaloceanspaces.com/sites/artly/templates/";

// Image card component for gallery
function ImageCard({ image, isSelected, onClick }) {
    return (
        <div
            className={`vtImageCard ${isSelected ? 'vtImageCardSelected' : ''}`}
            onClick={onClick}
        >
            <img src={image.url} alt="" />
            {isSelected && <div className="vtImageCardCheck">&#10003;</div>}
        </div>
    );
}

// Empty photo card for when user has no photos
function EmptyPhotoCard({ onClick }) {
    return (
        <div className="vtEmptyCard" onClick={onClick}>
            <div className="vtEmptyCardIcon">+</div>
            <div className="vtEmptyCardText">Generate photo</div>
        </div>
    );
}

// Gallery tab content
function GalleryTabContent({
    config,
    selectedImage,
    setSelectedImage,
    userPhotos,
    onGeneratePhotoClick
}) {
    const handleImageClick = (imageData) => {
        if (selectedImage?.url === imageData.url) {
            setSelectedImage(null);
        } else {
            setSelectedImage(imageData);
        }
    };

    return (
        <div className="vtGalleryContent">
            <div className="vtImageGrid">
                {config.sampleImages.map((image, index) => (
                    <ImageCard
                        key={image.id}
                        image={image}
                        isSelected={selectedImage?.url === image.url}
                        onClick={() => handleImageClick({
                            type: 'sample',
                            url: image.url,
                            sampleIdx: index + 1
                        })}
                    />
                ))}
            </div>

            <div className="vtDivider" />

            <div className="vtUserSection">
                <div className="vtUserTitle">Your photos</div>
                <div className="vtImageGrid">
                    {userPhotos.length > 0 ? (
                        userPhotos.map((photo, index) => (
                            <ImageCard
                                key={photo.media_id || index}
                                image={{ id: photo.media_id, url: photo.url }}
                                isSelected={selectedImage?.url === photo.url}
                                onClick={() => handleImageClick({
                                    type: 'user',
                                    url: photo.url,
                                    mediaId: photo.media_id
                                })}
                            />
                        ))
                    ) : (
                        <EmptyPhotoCard onClick={onGeneratePhotoClick} />
                    )}
                </div>
            </div>
        </div>
    );
}

// Choice chip component (reused from GenerationControls)
function ChoiceChip({ text, selected, onClick }) {
    return (
        <div
            className={selected ? "choiceChipSelected" : "choiceChip"}
            onClick={onClick}
        >
            {text}
        </div>
    );
}

// Control input row
function ControlInputRow({ header, options, formRef, rowIndex, stages, currentStage, stageRefs, getGenerateEl }) {
    const [, forceUpdate] = useState(0);
    const selectedIdx = formRef.current[stages.current[rowIndex].name];

    const handleSelect = (optionIndex, option) => {
        if (stages.current.length > rowIndex + 1) {
            for (let i = rowIndex + 1; i < stages.current.length; i++) {
                const stageName = stages.current[i].name;
                if (formRef.current.hasOwnProperty(stageName)) {
                    delete formRef.current[stageName];
                }
            }
            stages.current = stages.current.filter((_, idx) => idx <= rowIndex);
        }

        if (option.next !== "generate") {
            // Add next stage and scroll it into view once it renders.
            stages.current = [...stages.current, formSchemaVideo[option.next]];
            stages.forceUpdate();
            scrollNearAfterRender(() => stageRefs?.current?.[rowIndex + 1]);
        } else {
            // Last row answered — scroll down to the Generate button.
            scrollNearAfterRender(() => (getGenerateEl ? getGenerateEl() : null));
        }

        formRef.current[stages.current[rowIndex].name] = optionIndex;
        currentStage.current = option.next;
        currentStage.forceUpdate();
        forceUpdate(prev => prev + 1);
    };

    return (
        <div className="controlCard" ref={el => { if (stageRefs) stageRefs.current[rowIndex] = el; }}>
            <div className="controlInputRow">
                <div className="controlRowHeader">{header}</div>
                <div className="controlChips">
                    {options.map((option, index) => (
                        <ChoiceChip
                            key={index}
                            text={option.text}
                            selected={selectedIdx === index}
                            onClick={() => handleSelect(index, option)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

// Custom tab content with ControlForm for remaining stages
function CustomTabContent({
    token,
    user,
    formRef,
    setVideo,
    setGenType,
    setGenerated,
    setTaskId,
    initialStages,
    currentStage,
    genType,
    resultsRef,
    setShowLoginPopup
}) {
    const [upd, setForceUpdate] = useState(0);
    const stages = useRef(initialStages);
    const stageRefs = useRef({});
    const generateButtonRef = useRef(null);
    const errorRef = useRef(null);

    stages.forceUpdate = () => {
        setForceUpdate(prev => prev + 1);
    };

    // Use photo generate hook for custom tab (form-based generation)
    const { handleGenerate, error } = usePhotoGenerate({
        token,
        formRef,
        textPromptActive: false,
        textPromptValue: '',
        callbacks: {
            setGenType,
            setGenerated,
            setImg: () => {},
            setVideo,
            setTaskId,
            setAnimateAction: () => {},
            setGenerationConfig: () => {},
            setShowVideoPopUp: () => {},
            setShowPayPopUp: () => {},
            onScrollToResults: () => {
                resultsRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            },
            onResetInteraction: () => {}
        }
    });

    const showGenerateButton = currentStage.current === "generate";

    // Scroll to error when it appears
    useEffect(() => {
        if (error) scrollNear(errorRef.current, 40);
    }, [error]);

    const handleGenerateClick = () => {
        if (!user) {
            if (setShowLoginPopup) setShowLoginPopup(true);
            return;
        }
        handleGenerate();
    };

    return (
        <div className="vtCustomContent">
            <div className="controlFormSection">
                {stages.current.map((stage, index) => (
                    <ControlInputRow
                        key={stage.name + '-' + index}
                        header={stage.header}
                        options={stage.options}
                        formRef={formRef}
                        rowIndex={index}
                        stages={stages}
                        currentStage={currentStage}
                        stageRefs={stageRefs}
                        getGenerateEl={() => generateButtonRef.current}
                    />
                ))}
            </div>

            {showGenerateButton && (
                <div className="vtButtonArea" ref={generateButtonRef}>
                    <RedButton onClick={handleGenerateClick} disabled={genType}>
                        {genType ? "Generating..." : "Generate"}
                    </RedButton>
                    {error && <div className="vtError" ref={errorRef}>{error}</div>}
                </div>
            )}
        </div>
    );
}

// Tab component
function Tab({ label, active, onClick }) {
    return (
        <button
            className={`vtTab ${active ? 'vtTabActive' : ''}`}
            onClick={onClick}
        >
            {label}
        </button>
    );
}

export default function VideoTemplate({ token, user, setSignedIn, setShowLoginPopup }) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const templateId = searchParams.get('template') || 'reverse_cowgirl';

    // Get config for this template
    const config = videoActionsConfig[templateId] || defaultVideoAction;

    // Find the path to this action in the video schema
    const actionPath = useMemo(() => findActionPath(config.action, formSchemaVideo), [config.action]);

    // Build initialStages array - only contains ethnicity stage
    const initialStages = useMemo(() => {
        if (actionPath) {
            const ethnicityStage = formSchemaVideo[actionPath.actionNext];
            return ethnicityStage ? [ethnicityStage] : [formSchemaVideo.stage1];
        }
        return [formSchemaVideo.stage1];
    }, [actionPath]);

    // Build initial formRef values with hidden stage selections
    const initialFormRefValues = useMemo(() => {
        if (actionPath) {
            return {
                type: "video",
                stage1: actionPath.stage1Index,
                [actionPath.stage1Next]: actionPath.actionIndex
            };
        }
        return { type: "video" };
    }, [actionPath]);

    // State
    const [activeTab, setActiveTab] = useState('gallery');
    const [selectedImage, setSelectedImage] = useState(null);
    const [userPhotos, setUserPhotos] = useState([]);

    // Generation state
    const [video, setVideo] = useState(null);
    const [genType, setGenType] = useState(null);
    const [generated, setGenerated] = useState(false);
    const [taskId, setTaskId] = useState(null);

    // Form ref for custom tab
    const formRef = useRef(initialFormRefValues);
    const currentStage = useRef(actionPath?.actionNext || "stage1");
    const [stageUpd, setStageUpd] = useState(0);
    const resultsRef = useRef(null);
    const galleryErrorRef = useRef(null);

    currentStage.forceUpdate = () => {
        setStageUpd(prev => prev + 1);
    };

    // Use the video animate hook for Gallery tab
    const { handleAnimate: handleGalleryGenerate, error: galleryError, setError: setGalleryError, loading: galleryLoading } = useVideoAnimate({
        token,
        user,
        selectedImage,
        action: config.action,
        callbacks: {
            setGenType,
            setGenerated,
            setVideo,
            setTaskId,
            setShowLoginPopup,
            onScrollToResults: () => {
                resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    });


    // Update formRef when initialFormRefValues changes
    useEffect(() => {
        Object.assign(formRef.current, initialFormRefValues);
    }, [initialFormRefValues]);

    // Set initial preview video
    useEffect(() => {
        setVideo(`${CDN_VIDEO_BASE}${templateId}.mp4`);
        setGenerated(true);
    }, [templateId]);

    // Fetch user photos
    useEffect(() => {
        if (token && user) {
            fetchUserPhotos(token, config.action).then(photos => {
                setUserPhotos(photos);
            }).catch(err => {
                console.error("Error fetching user photos:", err);
            });
        }
    }, [token, user, config.action]);

    // Scroll to the gallery error when it appears
    useEffect(() => {
        if (galleryError) scrollNear(galleryErrorRef.current, 40);
    }, [galleryError]);

    const handleGeneratePhotoClick = () => {
        navigate('/main');
    };

    const handleBackClick = () => {
        navigate('/video');
    };

    return (
        <div className="vtPage">
            <div className="vtResults" ref={resultsRef}>
                <ResultDisplay
                    images={null}
                    video={video}
                    selectedIndex={0}
                    onSelectIndex={() => {}}
                    genType={genType}
                    generated={generated}
                />
            </div>

            <div className="vtSidebar">
                <div className="vtHeader">
                    <BackArrow onClick={handleBackClick} />
                    <div className="vtHeaderText">
                        <h1 className="vtTitle">{config.title}</h1>
                        <p className="vtSubtitle">{config.subtitle}</p>
                    </div>
                </div>

                <div className="vtTabs">
                    <Tab
                        label="Gallery"
                        active={activeTab === 'gallery'}
                        onClick={() => setActiveTab('gallery')}
                    />
                    <Tab
                        label="Custom"
                        active={activeTab === 'custom'}
                        onClick={() => setActiveTab('custom')}
                    />
                </div>

                <div className="vtTabContent">
                    {activeTab === 'gallery' ? (
                        <>
                            <GalleryTabContent
                                config={config}
                                selectedImage={selectedImage}
                                setSelectedImage={setSelectedImage}
                                userPhotos={userPhotos}
                                onGeneratePhotoClick={handleGeneratePhotoClick}
                            />
                            <div className="vtButtonArea">
                                <RedButton onClick={handleGalleryGenerate} disabled={galleryLoading || genType}>
                                    {genType ? "Generating..." : "Generate"}
                                </RedButton>
                                {galleryError && <div className="vtError" ref={galleryErrorRef}>{galleryError}</div>}
                            </div>
                        </>
                    ) : (
                        <CustomTabContent
                            token={token}
                            user={user}
                            formRef={formRef}
                            setVideo={setVideo}
                            setGenType={setGenType}
                            setGenerated={setGenerated}
                            setTaskId={setTaskId}
                            initialStages={initialStages}
                            currentStage={currentStage}
                            genType={genType}
                            resultsRef={resultsRef}
                            setShowLoginPopup={setShowLoginPopup}
                        />
                    )}
                </div>

                <div className="vtFooter">
                    <div className="vtDisclaimer">
                        All generated content is AI-created. Users must be 18+ and agree to our terms.
                    </div>
                    <SupportEmail />
                    <div className="vtFooterLinks">
                        <a href="/terms" className="vtFooterLink">Terms</a>
                        <a href="/privacy" className="vtFooterLink">Privacy</a>
                        <a href="/take_it_down_report" className="vtFooterLink">Report content</a>
                        <a href="https://discord.gg/genfun" className="vtFooterLink" target="_blank" rel="noopener noreferrer">Discord</a>
                    </div>
                    <LegalNotice />
                </div>
            </div>
        </div>
    );
}
